import { useMemo, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { useApi } from '../../hooks/useApi.js';
import { Button } from '../../components/Button.jsx';

const INK = '#F5EEDC';

export const InkDensityIndexPage = () => {
  const [search, setSearch] = useState('');
  const { route } = useLocation();

  const qs = search ? `?search=${encodeURIComponent(search)}` : '';
  const { data: readings, loading, error } = useApi(`/ink-density${qs}`);
  const { data: comparisons } = useApi('/ink-density/comparisons');

  return (
    <div>
      <section class="mb-5 flex items-start justify-between gap-4">
        <div>
          <div class="mb-1.5 flex items-center gap-2">
            <span class="block h-2.5 w-2.5 rounded-full" style={{ background: INK, border: '1px solid rgba(245,238,220,0.4)' }} />
            <span class="text-[11px] font-semibold uppercase tracking-eyebrow" style={{ color: INK }}>Ink density</span>
          </div>
          <h1 class="m-0 mb-1.5 text-[30px] leading-[1.18] tracking-tight text-text-primary">Density readings</h1>
          <p class="m-0 text-sm text-text-secondary">CMYK density + gradation step readings per job. Linked to customer profiles.</p>
        </div>
        <div class="flex gap-2">
          <Button variant="secondary" onClick={() => route('/ink-density/comparisons')}>Comparisons</Button>
          <Button variant="primary" onClick={() => route('/ink-density/new')}>+ New reading</Button>
        </div>
      </section>

      <div class="mb-4 flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={search}
          onInput={(e) => setSearch(e.currentTarget.value)}
          placeholder="Search by job number, name, or customer…"
          class="block w-80 max-w-full rounded-md border border-border-strong bg-bg-card px-3 py-1.5 text-sm placeholder:text-text-muted focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
        />
        <span class="ml-auto text-[12px] text-text-tertiary">{readings ? `${readings.length} reading${readings.length === 1 ? '' : 's'}` : ''}</span>
      </div>

      {loading && <p class="text-sm text-text-tertiary">Loading…</p>}
      {error && (
        <div class="rounded-md border border-border-strong bg-bg-card p-3 text-sm text-text-secondary">
          Couldn't load — {error.message || 'request failed'}.
        </div>
      )}
      {readings && readings.length === 0 && !loading && (
        <div class="surface-card p-6 text-center">
          <p class="m-0 font-serif text-lg font-medium text-text-primary">No readings yet.</p>
          <p class="m-0 mt-1 text-sm text-text-secondary">Start one from a Customer Profile or use "+ New reading".</p>
        </div>
      )}
      {readings && readings.length > 0 && (
        <div class="space-y-1.5">
          {readings.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => route(`/ink-density/${r.id}`)}
              class="relative block w-full overflow-hidden rounded-md border border-border-subtle bg-bg-card p-3 text-left transition hover:border-border-strong hover:bg-bg-hover"
            >
              <span class="pointer-events-none absolute inset-y-0 left-0 w-[3px]" style={{ background: INK }} aria-hidden="true" />
              <div class="flex flex-wrap items-baseline justify-between gap-2">
                <div>
                  <span class="font-mono text-[13px] font-semibold text-text-primary">{r.jobNumber || `#${r.id}`}</span>
                  {r.jobName && <span class="ml-2 text-[12px] text-text-secondary">{r.jobName}</span>}
                </div>
                <span class="font-mono text-[11px] text-text-tertiary">{r.readingDate || ''}</span>
              </div>
              <div class="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-text-tertiary">
                <span>{r.customerName}</span>
                {r.profileLabel && <span>· {r.profileLabel}</span>}
                {r.printType && <span class="rounded-full border border-border-tertiary px-1.5 py-0 font-mono">{r.printType}</span>}
                {(r.tags ?? []).slice(0, 4).map((t) => (
                  <span key={t} class="rounded-full bg-bg-base px-1.5 py-0 font-mono text-[10px]">{t}</span>
                ))}
              </div>
            </button>
          ))}
        </div>
      )}

      {comparisons && comparisons.length > 0 && (
        <section class="mt-8">
          <h2 class="mb-2 text-[11px] font-semibold uppercase tracking-eyebrow text-text-tertiary">Recent comparisons</h2>
          <div class="space-y-1.5">
            {comparisons.slice(0, 5).map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => route(`/ink-density/comparisons/${c.id}`)}
                class="block w-full rounded-lg border border-border-subtle bg-bg-card p-3 text-left hover:border-border-strong"
              >
                <span class="font-serif text-[14px] text-text-primary">{c.name}</span>
                <span class="ml-2 text-[11px] text-text-tertiary">{c.readingCount} reading{c.readingCount === 1 ? '' : 's'}</span>
              </button>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};
